import { createAsyncThunk } from '@reduxjs/toolkit';
import { useCustomAxios } from '../../shared/lib/customAxios';
import { API_ENDPOINTS, UPLOAD_FILE_PURPOSE_TYPE } from '../../shared/constants/constants';

// A request that never received a response - DNS failure, CORS rejection,
// timeout, a gateway returning no body - has no `error.response` at all.
// Reading `error.response.data.message` on it threw a second TypeError from
// inside the catch, which rejected the thunk and surfaced to the caller as a
// bare "Error Uploading File." with the real cause discarded. That is how a
// misconfigured upload Lambda looked like an unexplained frontend failure.
// Returns `any` deliberately: these thunks' payloads are consumed as `any`
// across ~30 call sites, and giving this a narrow shape would narrow the
// thunk union and break every one of them. Widening the types is worth doing,
// but not inside a bug fix.
const toErrorPayload = (error: any): any => ({
  error:
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.message ??
    'Request failed',
  status: error?.response?.status ?? 0,
});


export const getUploadPresignedUrl = createAsyncThunk( "upload/presignUrl" ,async (reqBody: any) => {
  // console.log(reqBody)
  const { axiosPost } = useCustomAxios();
  try {
    const response = await axiosPost(API_ENDPOINTS.fileUploadPresignedUrl, reqBody);
    return { data: response.data, status: response.status };
  } catch (error: any) {
    // console.log(error);
    return toErrorPayload(error);
  }
})


export const uploadFileToS3 = createAsyncThunk( "upload/S3" ,async ({uploadUrl, file, contentType}:{uploadUrl: string, file: File, contentType: string}) => {
  try {
    const { axiosPut } = useCustomAxios();
    const response = await axiosPut(uploadUrl, file, {
      headers: {
        'Content-Type': contentType, // Set the correct MIME type
      },
    });

    return response; // Return response to check if upload was successful
  } catch (err) {
    console.error('Error uploading to S3:', err);
    return { status: 500, error: 'Upload failed' }; // Return an error response
  }
});

export const uploadFile = createAsyncThunk(
  'uploadRFQItemsFile',
  async (reqBody: any) => {
    // console.log(reqBody)
    const { axiosPost } = useCustomAxios();
    try {
      const response = await axiosPost(API_ENDPOINTS.fileUpload, reqBody
      );
      return { data: response.data, status: response.status };
    } catch (error: any) {
      // console.log(error);
      return toErrorPayload(error);
    }
  }
);

export const uploadOrderInvoiceFile = createAsyncThunk(
  'uploadorderinvoice',
  async (reqBody: any, { rejectWithValue }) => {
    // console.log(reqBody)
    const { axiosPost } = useCustomAxios();
    try {
      const response = await axiosPost(API_ENDPOINTS.fileUpload, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      // console.log(error);
      return rejectWithValue(error?.response?.data ?? { message: error?.message ?? 'Request failed' })
    }
  }
);

export const uploadInvoiceFile = createAsyncThunk(
  'uploadInvoiceFile',
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();
    try {
      const response = await axiosPost(API_ENDPOINTS.invoiceFileUpload, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.message,
        status: error.response?.status,
      };
    }
  }
);

export const uploadFileParty = createAsyncThunk(
  'uploadPicture',
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();
    try {
      const response = await axiosPost(API_ENDPOINTS.uploadFileParty, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return toErrorPayload(error);
    }
  }
);

export const downloadPdfFile = createAsyncThunk(
  'downloadPdfFile',
  async (data: any) => {
    const { axiosPost } = useCustomAxios();
    const { url, reqBody } = data;
    try {
      const response = await axiosPost(url, reqBody);
      return { data: response.data, status: response.status };
    }
    catch (error: any) {
      return toErrorPayload(error);
    }
  }
)


export const uploadRequestContentApi = createAsyncThunk(
  "buyer/uploadRFQContents",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(`${API_ENDPOINTS.uploadRFQContents}`, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return toErrorPayload(error);
    }
  }
);
export const deleteContentApi = createAsyncThunk(
  "buyer/deleteContent",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(`${API_ENDPOINTS.deleteContent}`, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return toErrorPayload(error);
    }
  }
);
export const uploadQuoteContentsApi = createAsyncThunk(
  "buyer/uploadQuoteContents",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(`${API_ENDPOINTS.uploadQuoteContents}?filePurposeType=${UPLOAD_FILE_PURPOSE_TYPE.QUOTE_DOCS}`, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return toErrorPayload(error);
    }
  }
);